import api, { brl } from "./api";
import { toast } from "sonner";

/**
 * Fetch autenticado do PDF + validação de magic bytes %PDF.
 * Retorna { blob, url } ou lança Error com mensagem amigável.
 */
async function fetchAuthPdf(path) {
  let resp;
  try {
    resp = await api.get(path, { responseType: "blob" });
  } catch (e) {
    // Erros HTTP: o body pode ser JSON com { detail }
    let detail = "Erro ao gerar PDF.";
    try {
      const errBlob = e?.response?.data;
      if (errBlob && errBlob.text) {
        const txt = await errBlob.text();
        try {
          detail = JSON.parse(txt)?.detail || txt || detail;
        } catch {
          detail = txt || detail;
        }
      } else {
        detail = e?.response?.data?.detail || detail;
      }
    } catch { /* keep default */ }
    throw new Error(detail);
  }

  const blob = resp.data;
  // Ler os 4 primeiros bytes para validar %PDF
  const headBuf = await blob.slice(0, 4).arrayBuffer();
  const head = new TextDecoder().decode(new Uint8Array(headBuf));
  if (head !== "%PDF") {
    // Servidor devolveu JSON/HTML/texto no lugar do PDF
    let detail = "Servidor não devolveu um PDF válido.";
    try {
      const txt = await blob.text();
      try {
        detail = JSON.parse(txt)?.detail || detail;
      } catch {
        detail = txt.slice(0, 300) || detail;
      }
    } catch { /* keep default */ }
    throw new Error(detail);
  }
  const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
  return { blob, url };
}

/**
 * Cria um modal com iframe embutido para exibir o PDF, com botão
 * "Baixar" como alternativa (útil em iOS/WebView que não renderizam iframes).
 */
function openPdfModal({ url, filename, title }) {
  // Remover modal anterior se existir
  const prev = document.getElementById("pdf-viewer-modal");
  if (prev) prev.remove();

  const overlay = document.createElement("div");
  overlay.id = "pdf-viewer-modal";
  overlay.setAttribute("data-testid", "pdf-viewer-modal");
  overlay.style.cssText = [
    "position:fixed", "inset:0", "background:rgba(0,0,0,0.75)",
    "z-index:9999", "display:flex", "align-items:center",
    "justify-content:center", "padding:12px",
  ].join(";");

  const modal = document.createElement("div");
  modal.style.cssText = [
    "background:#fff", "width:100%", "max-width:960px",
    "height:92vh", "display:flex", "flex-direction:column",
    "border-radius:6px", "overflow:hidden",
    "box-shadow:0 10px 40px rgba(0,0,0,0.4)",
  ].join(";");

  const header = document.createElement("div");
  header.style.cssText = [
    "display:flex", "align-items:center", "justify-content:space-between",
    "padding:10px 14px", "border-bottom:1px solid #e5e7eb",
    "background:#0A1628", "color:#fff",
  ].join(";");

  const titleEl = document.createElement("div");
  titleEl.textContent = title || "Visualizar PDF";
  titleEl.style.cssText = "font-weight:600;font-size:14px;letter-spacing:0.02em";

  const actions = document.createElement("div");
  actions.style.cssText = "display:flex;gap:8px;align-items:center";

  const dlLink = document.createElement("a");
  dlLink.href = url;
  dlLink.download = filename || "documento.pdf";
  dlLink.rel = "noopener";
  dlLink.textContent = "Baixar PDF";
  dlLink.setAttribute("data-testid", "pdf-viewer-download");
  dlLink.style.cssText = [
    "background:#2563EB", "color:#fff", "padding:6px 12px",
    "font-size:13px", "border-radius:4px", "text-decoration:none",
    "font-weight:600",
  ].join(";");

  const openLink = document.createElement("a");
  openLink.href = url;
  openLink.target = "_blank";
  openLink.rel = "noopener noreferrer";
  openLink.textContent = "Abrir em nova aba";
  openLink.setAttribute("data-testid", "pdf-viewer-open-new-tab");
  openLink.style.cssText = [
    "background:#334155", "color:#fff", "padding:6px 12px",
    "font-size:13px", "border-radius:4px", "text-decoration:none",
    "font-weight:600",
  ].join(";");

  const closeBtn = document.createElement("button");
  closeBtn.textContent = "×";
  closeBtn.setAttribute("aria-label", "Fechar");
  closeBtn.setAttribute("data-testid", "pdf-viewer-close");
  closeBtn.style.cssText = [
    "background:transparent", "color:#fff", "border:none",
    "font-size:26px", "line-height:1", "cursor:pointer",
    "padding:0 6px",
  ].join(";");

  actions.appendChild(dlLink);
  actions.appendChild(openLink);
  actions.appendChild(closeBtn);
  header.appendChild(titleEl);
  header.appendChild(actions);

  const iframe = document.createElement("iframe");
  iframe.src = url;
  iframe.setAttribute("data-testid", "pdf-viewer-iframe");
  iframe.style.cssText = "flex:1;width:100%;border:none;background:#525659";
  iframe.title = title || "PDF";

  modal.appendChild(header);
  modal.appendChild(iframe);
  overlay.appendChild(modal);

  const cleanup = () => {
    setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
    overlay.remove();
    document.removeEventListener("keydown", onKey);
  };
  const onKey = (e) => { if (e.key === "Escape") cleanup(); };
  closeBtn.addEventListener("click", cleanup);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) cleanup(); });
  document.addEventListener("keydown", onKey);

  document.body.appendChild(overlay);
}

/**
 * Abre um PDF autenticado (DANFE, recibo, etc.).
 * DEVE ser chamada dentro de um handler de clique do usuário.
 * Fluxo:
 *   1. GET autenticado (axios blob) do PDF
 *   2. Valida magic bytes %PDF
 *   3. Exibe em modal com <iframe> + link <a download> alternativo
 * Em caso de erro, mostra toast com a mensagem do backend.
 */
export async function openPdf(path, filename = "documento.pdf", title) {
  try {
    const { url } = await fetchAuthPdf(path);
    openPdfModal({ url, filename, title: title || filename });
    return true;
  } catch (e) {
    toast.error(e.message || "Erro ao gerar PDF.");
    return false;
  }
}

/**
 * Abre um documento HTML autenticado (ex: recibo NFC-e do Focus).
 * DEVE ser chamada dentro de um handler de clique do usuário.
 * Fluxo:
 *   1. GET autenticado (axios blob) do HTML
 *   2. Valida que os bytes iniciais começam com '<'
 *   3. Exibe em modal com <iframe> embutido + link download alternativo
 */
export async function openHtmlDoc(path, filename = "documento.html", title) {
  let resp;
  try {
    resp = await api.get(path, { responseType: "blob" });
  } catch (e) {
    let detail = "Erro ao gerar HTML.";
    try {
      const errBlob = e?.response?.data;
      if (errBlob && errBlob.text) {
        const txt = await errBlob.text();
        try { detail = JSON.parse(txt)?.detail || txt || detail; }
        catch { detail = txt || detail; }
      } else {
        detail = e?.response?.data?.detail || detail;
      }
    } catch { /* keep default */ }
    toast.error(detail);
    return false;
  }
  const blob = resp.data;
  // Ler os primeiros bytes para verificar HTML
  const headBuf = await blob.slice(0, 200).arrayBuffer();
  const head = new TextDecoder().decode(new Uint8Array(headBuf)).trim();
  if (!head.startsWith("<")) {
    let detail = "Servidor não devolveu HTML válido.";
    try {
      const txt = await blob.text();
      try { detail = JSON.parse(txt)?.detail || detail; }
      catch { detail = txt.slice(0, 300) || detail; }
    } catch { /* keep default */ }
    toast.error(detail);
    return false;
  }
  const url = window.URL.createObjectURL(
    new Blob([blob], { type: "text/html;charset=utf-8" })
  );
  openPdfModal({ url, filename, title: title || filename });
  return true;
}

/* ---------- Cupom 80mm (impressão HTML direta) ---------- */

const escapeHtml = (s = "") =>
  String(s).replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;",
    '"': "&quot;", "'": "&#39;",
  }[c]));

const PAYMENT_LABEL = {
  dinheiro: "Dinheiro", pix: "PIX",
  cartao_credito: "Cartão Crédito", cartao_debito: "Cartão Débito",
  boleto: "Boleto", infinitepay: "InfinitePay",
};

function buildReceipt80mmHtml(sale, store = {}) {
  const items = (sale.items || []).map((i) => {
    const qty = i.quantity || 0;
    const unit = i.unit_price || 0;
    return `
      <div class="item">
        <div class="line">
          <span class="name">${escapeHtml(i.name || "Item")}</span>
        </div>
        <div class="line small">
          <span>${qty} x ${brl(unit)}</span>
          <span>${brl(qty * unit)}</span>
        </div>
      </div>`;
  }).join("");

  const dt = new Date();
  const dateStr = dt.toLocaleDateString("pt-BR") + " " +
    dt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const payLabel = PAYMENT_LABEL[sale.payment_method] || sale.payment_method || "-";
  const discount = Number(sale.discount) || 0;
  const subtotal = Number(sale.subtotal) || (Number(sale.total) + discount);

  return `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8"/>
<title>Cupom Venda</title>
<style>
  @page { size: 80mm auto; margin: 2mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: 'Courier New', ui-monospace, monospace;
    font-size: 12px; color: #000; width: 76mm;
    padding: 4px 2px;
  }
  .center { text-align: center; }
  .bold { font-weight: 700; }
  .h1 { font-size: 14px; font-weight: 700; }
  .small { font-size: 10.5px; }
  hr {
    border: none; border-top: 1px dashed #000;
    margin: 6px 0;
  }
  .line { display: flex; justify-content: space-between; gap: 6px; }
  .name { word-break: break-word; flex: 1; }
  .item { margin-bottom: 3px; }
  .tot { font-size: 14px; font-weight: 700; }
  .footer { margin-top: 8px; font-size: 10.5px; text-align: center; }
  @media print {
    body { width: auto; }
    .no-print { display: none !important; }
  }
</style></head>
<body>
  <div class="center">
    <div class="h1">${escapeHtml(store.store_name || "UP CEL Quilombo")}</div>
    ${store.address ? `<div class="small">${escapeHtml(store.address)}</div>` : ""}
    ${store.phone ? `<div class="small">Tel: ${escapeHtml(store.phone)}</div>` : ""}
    ${store.whatsapp_number ? `<div class="small">WhatsApp: ${escapeHtml(store.whatsapp_number)}</div>` : ""}
  </div>
  <hr/>
  <div class="small">
    <div><span class="bold">Venda:</span> #${escapeHtml((sale.id || "").slice(0, 8).toUpperCase())}</div>
    <div><span class="bold">Data:</span> ${escapeHtml(dateStr)}</div>
    <div><span class="bold">Cliente:</span> ${escapeHtml(sale.customer_name || "Consumidor")}</div>
  </div>
  <hr/>
  ${items}
  <hr/>
  <div class="line small"><span>Subtotal</span><span>${brl(subtotal)}</span></div>
  ${discount > 0 ? `<div class="line small"><span>Desconto</span><span>- ${brl(discount)}</span></div>` : ""}
  <div class="line tot"><span>TOTAL</span><span>${brl(sale.total || 0)}</span></div>
  <div class="small" style="margin-top:4px">Pagamento: ${escapeHtml(payLabel)}</div>
  <hr/>
  <div class="footer">
    Obrigado pela preferência!<br/>
    ${escapeHtml(store.store_name || "UP CEL Quilombo")}
  </div>
  <div class="no-print" style="text-align:center;margin-top:12px">
    <button onclick="window.print()" style="padding:8px 16px;font-size:13px;">Imprimir</button>
  </div>
</body></html>`;
}

/**
 * Renderiza um cupom 80mm em HTML dentro de um iframe oculto e
 * dispara window.print(). DEVE ser chamada num handler de clique.
 */
export function printReceipt80mm(sale, store = {}) {
  if (!sale) {
    toast.error("Venda não encontrada");
    return;
  }
  const html = buildReceipt80mmHtml(sale, store);
  // Iframe oculto para não perder o contexto do usuário
  const prev = document.getElementById("receipt-print-frame");
  if (prev) prev.remove();
  const frame = document.createElement("iframe");
  frame.id = "receipt-print-frame";
  frame.setAttribute("data-testid", "receipt-print-frame");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
  document.body.appendChild(frame);

  const doc = frame.contentDocument || frame.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();

  const doPrint = () => {
    try {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } catch (e) {
      toast.error("Não foi possível abrir a impressão.");
    }
    // Cleanup depois que o diálogo fecha (best-effort)
    setTimeout(() => {
      const f = document.getElementById("receipt-print-frame");
      if (f) f.remove();
    }, 15_000);
  };

  // Aguardar assets carregarem
  if (frame.contentWindow.document.readyState === "complete") {
    setTimeout(doPrint, 100);
  } else {
    frame.onload = () => setTimeout(doPrint, 100);
  }
}
